import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  getCountFromServer,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import {
  FaBuilding,
  FaClipboardCheck,
  FaDollarSign,
  FaDownload,
  FaFilter,
  FaHourglassHalf,
  FaSync,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { firestoreDev, firestoreProd } from "../../firebase";

// ─── Constants ───────────────────────────────────────────────────────────────

// Completed processing-checklist run marker (mirrors LoanHistory.TYPES).
// Counted per org via the same (organizationId, type) history index the
// Loan Stage / PC pages already rely on.
const CHECKLIST_RUN_COMPLETED_TYPE = "checklist_run_completed";

// Known acquisition sources. `all` loads every tagged org and filters in memory
// (Firestore cannot query for "field exists"); named sources use an equality
// query on the single-field index.
const SOURCE_OPTIONS = [
  { value: "radcrm", label: "RadCRM" },
  { value: "all", label: "All tagged sources" },
];

const ADDY_INTERNAL_DOMAINS = ["addy-ai.com", "addy.com", "heyaddy.com"];

// The single billable "The RAD CRM" organization. RADCRM signups are mirrored
// into it as `radcrmMirrored` member rows tagged with the customer org they
// came from, and those rows are what the partner is actually invoiced for.
// IDs differ per environment; keep in sync with
// backend/functions/config/viaBrandingSourceOrgs.js.
const RAD_CRM_SOURCE_ORG_ID = {
  prod: "5Ht3B3JxzpPCBJK8gilM",
  dev: "bBuZCTkkwrpV9RUv6mEk",
};

// Per-seat monthly prices. Keep in sync with
// backend/functions/services/radCrmPartnerBilling.js.
const STANDARD_SEAT_PRICE = 200;
const ASSISTANT_SEAT_PRICE = 30;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const isAddyTeam = (email) => {
  if (!email) return false;
  const lower = email.toLowerCase();
  return ADDY_INTERNAL_DOMAINS.some((d) => lower.endsWith("@" + d));
};

const tsToIso = (v) => {
  if (!v) return "";
  if (typeof v === "number") return new Date(v).toISOString();
  if (typeof v === "string") return v;
  if (v.seconds) return new Date(v.seconds * 1000).toISOString();
  if (v._seconds) return new Date(v._seconds * 1000).toISOString();
  if (typeof v.toDate === "function") return v.toDate().toISOString();
  return String(v);
};

const orgDisplayName = (org) =>
  org.name || org.organizationName || org.companyName || org.id;

/** Plan bucket for an active subscription (mirrors OrgUsersReport / EnterpriseOrgs). */
const getPlan = (subscription) => {
  if (!subscription || subscription.status !== "active") return "FREE";
  const p = subscription.priceId || "";
  if (p.includes("enterprise")) return "ENTERPRISE";
  if (p.includes("growth")) return "GROWTH";
  if (p.includes("essentials")) return "ESSENTIALS";
  if (p.startsWith("price_")) return "GROWTH";
  return "FREE";
};

// ─── Free-trial helpers ──────────────────────────────────────────────────────
// Mirrors backend/functions/utils/radCrmTrial.js. The trial is anchored to the
// customer ORGANIZATION: it starts when the RADCRM-sourced org is created and
// covers everyone in it. A month is billable only when it contains no trial
// time at all, so a trial ending Feb 9 makes February free and March the first
// invoiced month. Orgs with no `radcrmTrial` predate the feature and are billed
// as they always were.

/** Epoch millis for a Firestore Timestamp / Date / number, or null. */
const trialEndsAtOf = (org) => {
  const raw = org?.radcrmTrial?.endsAt;
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (raw instanceof Date) return raw.getTime();
  if (typeof raw.toMillis === "function") return raw.toMillis();
  if (typeof raw.toDate === "function") return raw.toDate().getTime();
  if (typeof raw.seconds === "number") return raw.seconds * 1000;
  if (typeof raw._seconds === "number") return raw._seconds * 1000;
  const parsed = new Date(raw).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Absolute month number so month arithmetic never wraps a year incorrectly. */
const monthIndex = (year, zeroBasedMonth) => year * 12 + zeroBasedMonth;

/**
 * First month this org's seats are billable, as an absolute month index.
 * The month the trial ends in is free because it contains trial days — unless
 * the trial ends exactly at a month boundary, leaving that month fully
 * billable. Matches `firstBillableMonthKey` on the backend.
 */
const firstBillableMonthIndex = (trialEndsAt) => {
  const d = new Date(trialEndsAt);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const base = monthIndex(year, month);
  return trialEndsAt > Date.UTC(year, month, 1) ? base + 1 : base;
};

/**
 * How many monthly invoices this org has been part of so far. The cron bills
 * the PREVIOUS calendar month on the 1st, so the newest invoiced month is last
 * month. Null when the org has no trial (billing history predates the feature
 * and cannot be derived).
 */
const monthsBilledFor = (org, now = new Date()) => {
  const trialEndsAt = trialEndsAtOf(org);
  if (trialEndsAt === null) return null;

  const lastInvoiced = monthIndex(now.getUTCFullYear(), now.getUTCMonth()) - 1;
  return Math.max(0, lastInvoiced - firstBillableMonthIndex(trialEndsAt) + 1);
};

/** Whole days until the trial ends, rounded up. Null when there is no trial. */
const trialDaysLeft = (org, now = Date.now()) => {
  const trialEndsAt = trialEndsAtOf(org);
  if (trialEndsAt === null) return null;
  return Math.ceil((trialEndsAt - now) / MS_PER_DAY);
};

/** Seat tier for a mirrored member row; guests are not billed. */
const seatTierOf = (member) => {
  const seatType = member.seatType || "";
  if (seatType === "guest" || member.role === "Loan Guest") return "guest";
  if (seatType === "assistant" || member.role === "Loan Officer Assistant") {
    return "assistant";
  }
  return "standard";
};

/**
 * Best-effort monthly amount from the STORED subscription snapshot only (no
 * Stripe call). Stripe snapshots vary, so we probe the common numeric fields
 * and treat large integers as cents. Returns null when nothing usable is
 * stored — the UI then shows the plan label instead of a fabricated number.
 */
const getStoredAmount = (subscription) => {
  if (!subscription || typeof subscription !== "object") return null;
  const candidates = [
    subscription.amount,
    subscription.unitAmount,
    subscription.unit_amount,
    subscription.planAmount,
    subscription.plan?.amount,
    subscription.price?.unit_amount,
    subscription.items?.[0]?.price?.unit_amount,
  ];
  for (const raw of candidates) {
    const n = typeof raw === "string" ? Number(raw) : raw;
    if (typeof n === "number" && Number.isFinite(n) && n > 0) {
      // Heuristic: Stripe stores money in cents; values >= 1000 are almost
      // certainly cents (>= $10), sub-1000 are treated as whole dollars.
      return n >= 1000 ? n / 100 : n;
    }
  }
  return null;
};

const fmtAmount = (amount) =>
  amount == null
    ? "—"
    : amount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });

async function fetchUsersByEmail(db, emails) {
  const byEmail = new Map();
  const unique = [
    ...new Set(emails.filter(Boolean).map((e) => e.toLowerCase())),
  ];
  for (const group of chunk(unique, 10)) {
    const snap = await getDocs(
      query(collection(db, "users"), where("email", "in", group)),
    );
    snap.forEach((d) => {
      const data = d.data();
      byEmail.set((data.email || "").toLowerCase(), { id: d.id, ...data });
    });
  }
  return byEmail;
}

/** Load tagged orgs for a source. `all` filters in memory for any tag. */
async function loadSourcedOrgs(db, source) {
  let docs;
  if (source && source !== "all") {
    const snap = await getDocs(
      query(
        collection(db, "organizations"),
        where("acquisitionSource", "==", source),
      ),
    );
    docs = snap.docs;
  } else {
    const snap = await getDocs(collection(db, "organizations"));
    docs = snap.docs.filter((d) => !!d.data()?.acquisitionSource);
  }

  return docs
    .map((d) => {
      const data = d.data() || {};
      return {
        id: d.id,
        name: orgDisplayName({ id: d.id, ...data }),
        subscription: data.subscription || null,
        acquisitionSource: data.acquisitionSource || "",
        acquisitionViaCode: data.acquisitionViaCode || "",
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Completed processing-checklist runs for one org (count-only, no doc reads). */
async function countChecklistRuns(db, organizationId) {
  try {
    const snap = await getCountFromServer(
      query(
        collectionGroup(db, "history"),
        where("organizationId", "==", organizationId),
        where("type", "==", CHECKLIST_RUN_COMPLETED_TYPE),
      ),
    );
    return snap.data().count;
  } catch (err) {
    // Missing index / rules → best-effort: surface as unknown rather than fail.
    // eslint-disable-next-line no-console
    console.warn(`[Signups] PC-run count failed for ${organizationId}:`, err);
    return null;
  }
}

/**
 * Load every billable mirrored seat on the RAD CRM org in ONE query and group
 * it by the customer organization it came from.
 *
 * These rows — not the customer org's own member list — are what the partner is
 * invoiced for, so they are the only accurate source for "what does this org
 * cost per month". Owners in particular have no member row in their own org but
 * do hold a mirrored seat.
 *
 * @returns {Promise<Map<string, {standard: number, assistant: number, monthly: number}>>}
 *   Source org ID -> seat counts and monthly amount. Empty when the mirror org
 *   is unreachable, in which case callers fall back to the stored subscription.
 */
async function loadMirroredSeatsBySourceOrg(db, env) {
  const mirrorOrgId = RAD_CRM_SOURCE_ORG_ID[env];
  const byOrg = new Map();
  if (!mirrorOrgId) return byOrg;

  let snap;
  try {
    snap = await getDocs(
      query(
        collection(db, "organization-members"),
        where("organizationId", "==", mirrorOrgId),
        where("status", "==", "accepted"),
        where("radcrmMirrored", "==", true),
      ),
    );
  } catch (err) {
    // Missing index / rules → fall back rather than failing the whole report.
    // eslint-disable-next-line no-console
    console.warn("[Signups] Mirrored seat load failed:", err);
    return byOrg;
  }

  snap.forEach((d) => {
    const member = d.data() || {};
    if (isAddyTeam(member.email)) return;

    const tier = seatTierOf(member);
    if (tier === "guest") return;

    const sourceOrgId = member.sourceOrganizationId || null;
    if (!sourceOrgId) return;

    if (!byOrg.has(sourceOrgId)) {
      byOrg.set(sourceOrgId, { standard: 0, assistant: 0, monthly: 0 });
    }
    const entry = byOrg.get(sourceOrgId);
    if (tier === "assistant") {
      entry.assistant += 1;
      entry.monthly += ASSISTANT_SEAT_PRICE;
    } else {
      entry.standard += 1;
      entry.monthly += STANDARD_SEAT_PRICE;
    }
  });

  return byOrg;
}

/**
 * Build the per-ORGANIZATION report for a source. One row per customer org,
 * carrying its people, free-trial state, how many months it has been invoiced,
 * and what it pays per month.
 *
 * Billing figures come from the mirrored seats the partner is actually charged
 * for; orgs with no mirrored seats (other acquisition sources, or RADCRM orgs
 * whose mirror rows predate source-org tracking) fall back to the stored
 * subscription amount, which is flagged so the UI does not present a guess as a
 * billed figure.
 */
async function buildReport(db, source, env) {
  const orgs = await loadSourcedOrgs(db, source);
  if (!orgs.length) return { rows: [] };

  const seatsByOrg = await loadMirroredSeatsBySourceOrg(db, env);
  const rows = [];

  for (const org of orgs) {
    const membersSnap = await getDocs(
      query(
        collection(db, "organization-members"),
        where("organizationId", "==", org.id),
      ),
    );
    const members = membersSnap.docs.map((d) => ({ _id: d.id, ...d.data() }));

    const [usersByEmail, pcRuns] = await Promise.all([
      fetchUsersByEmail(
        db,
        members.map((m) => m.email).filter(Boolean),
      ),
      countChecklistRuns(db, org.id),
    ]);

    const people = members.map((m) => {
      const email = (m.email || "").toLowerCase();
      const user = usersByEmail.get(email) || null;
      return {
        email: m.email || "",
        role: m.role || "",
        status: m.status || "",
        seatType: m.seatType || "",
        isAddy: m.isAddy === true || isAddyTeam(m.email),
        dateSignedUp: tsToIso(user && user.dateSignedUp),
      };
    });

    const seats = seatsByOrg.get(org.id) || null;
    const daysLeft = trialDaysLeft(org);
    const inTrial = daysLeft !== null && daysLeft > 0;

    rows.push({
      orgId: org.id,
      orgName: org.name,
      via: org.acquisitionViaCode || org.acquisitionSource,
      plan: getPlan(org.subscription),
      people,
      pcRuns,
      trialEndsAt: trialEndsAtOf(org),
      inTrial,
      trialDaysLeft: daysLeft,
      // A trialing org has not been invoiced yet, so months billed is 0 rather
      // than a negative count of months until billing starts.
      monthsBilled: inTrial ? 0 : monthsBilledFor(org),
      standardSeats: seats ? seats.standard : null,
      assistantSeats: seats ? seats.assistant : null,
      monthlyAmount: seats ? seats.monthly : getStoredAmount(org.subscription),
      amountIsEstimated: !seats,
    });
  }

  return { rows };
}

/**
 * CSV export: one row per person, each carrying its organization's trial and
 * billing columns. Flat rows keep the file pivot-friendly in a spreadsheet
 * while still answering the per-org questions the on-screen table shows.
 */
function downloadCsv(filename, orgRows, excludeAddy) {
  const headers = [
    "orgName",
    "orgId",
    "via",
    "email",
    "role",
    "status",
    "seatType",
    "isAddy",
    "onTrial",
    "trialEndsOn",
    "trialDaysLeft",
    "monthsBilled",
    "orgMonthlyUsd",
    "orgMonthlyIsEstimated",
    "orgStandardSeats",
    "orgAssistantSeats",
    "orgPcRuns",
    "dateSignedUp",
  ];
  const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;

  const lines = [];
  for (const org of orgRows) {
    const people = excludeAddy
      ? org.people.filter((p) => !p.isAddy)
      : org.people;
    for (const person of people) {
      lines.push(
        [
          org.orgName,
          org.orgId,
          org.via,
          person.email,
          person.role,
          person.status,
          person.seatType,
          person.isAddy ? "yes" : "no",
          org.inTrial ? "yes" : "no",
          org.trialEndsAt ? new Date(org.trialEndsAt).toISOString().slice(0, 10) : "",
          org.trialDaysLeft == null ? "" : org.trialDaysLeft,
          org.monthsBilled == null ? "" : org.monthsBilled,
          org.monthlyAmount == null ? "" : org.monthlyAmount,
          org.amountIsEstimated ? "yes" : "no",
          org.standardSeats == null ? "" : org.standardSeats,
          org.assistantSeats == null ? "" : org.assistantSeats,
          org.pcRuns == null ? "" : org.pcRuns,
          person.dateSignedUp,
        ]
          .map(esc)
          .join(","),
      );
    }
  }

  const csv = headers.join(",") + "\n" + lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Styles (mirrors OrgUsersReport visual language) ─────────────────────────

const S = {
  page: {
    padding: "28px 32px 48px",
    minHeight: "100vh",
    background: "#f8f9fa",
    width: "100%",
  },
  header: { marginBottom: 24 },
  h1: {
    fontSize: "1.5rem",
    fontWeight: 800,
    margin: "0 0 4px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "#111827",
  },
  h1Icon: {
    width: 34,
    height: 34,
    borderRadius: 9,
    background: "linear-gradient(135deg,#7f7fd5,#a78bfa)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    flexShrink: 0,
  },
  subtitle: { color: "#6b7280", fontSize: "0.85rem", margin: 0 },
  controls: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 14,
    marginBottom: 22,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  fieldsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    width: "100%",
  },
  fieldCol: { display: "flex", flexDirection: "column", gap: 5, minWidth: 0 },
  fieldLabel: {
    fontSize: "0.68rem",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 700,
  },
  fieldInput: {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#111827",
    fontSize: "0.875rem",
    outline: "none",
    boxSizing: "border-box",
  },
  actionRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: 12,
  },
  btnPrimary: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 16px",
    borderRadius: 8,
    border: "none",
    background: "#7f7fd5",
    color: "#fff",
    fontSize: "0.85rem",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnGhost: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 16px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#374151",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  toggleWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 14px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    color: "#374151",
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    userSelect: "none",
  },
  toggleInput: {
    margin: 0,
    cursor: "pointer",
    accentColor: "#7f7fd5",
    width: 14,
    height: 14,
  },
  toggleCount: { marginLeft: 6, color: "#9ca3af", fontWeight: 500 },
  statsRow: { display: "flex", gap: 14, marginBottom: 22, flexWrap: "wrap" },
  statCard: (color) => ({
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "14px 20px",
    minWidth: 150,
    flex: "1 1 150px",
    borderTop: `3px solid ${color}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  }),
  statLabel: {
    fontSize: "0.7rem",
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 4,
    fontWeight: 600,
  },
  statValue: (color) => ({
    fontSize: "1.6rem",
    fontWeight: 800,
    color,
    lineHeight: 1,
  }),
  statSub: { fontSize: "0.7rem", color: "#9ca3af", marginTop: 4 },
  tableCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  tableHeader: {
    padding: "12px 18px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  tableTitle: { fontWeight: 700, color: "#111827", fontSize: "0.95rem" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" },
  thead: { background: "#f9fafb", borderBottom: "2px solid #e5e7eb" },
  th: {
    padding: "11px 14px",
    textAlign: "left",
    fontWeight: 700,
    color: "#374151",
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  tr: { borderBottom: "1px solid #f3f4f6" },
  td: { padding: "12px 14px", verticalAlign: "middle", color: "#374151" },
  tdEmail: { fontWeight: 600, color: "#111827", whiteSpace: "nowrap" },
  tdMuted: { color: "#6b7280", whiteSpace: "nowrap", fontSize: "0.8rem" },
  empty: { textAlign: "center", padding: "60px 24px", color: "#9ca3af" },
};

const PILL = {
  yes: { bg: "#ecfdf5", fg: "#047857", border: "#a7f3d0" },
  no: { bg: "#fef2f2", fg: "#b91c1c", border: "#fecaca" },
  pending: { bg: "#fffbeb", fg: "#92400e", border: "#fde68a" },
  accepted: { bg: "#eff6ff", fg: "#1d4ed8", border: "#bfdbfe" },
  neutral: { bg: "#f3f4f6", fg: "#6b7280", border: "#e5e7eb" },
};

function Pill({ tone, children }) {
  const c = PILL[tone] || PILL.neutral;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: "0.72rem",
        fontWeight: 700,
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
      }}
    >
      {children}
    </span>
  );
}

/**
 * One organization: a summary row plus, when expanded, a nested table of its
 * people. Rendered as a fragment so both live directly under the same <tbody>
 * and stay column-aligned with the header.
 */
function OrgRows({ org, expanded, onToggle }) {
  const seatLabel =
    org.standardSeats == null
      ? "—"
      : [
          org.standardSeats ? `${org.standardSeats} std` : null,
          org.assistantSeats ? `${org.assistantSeats} LOA` : null,
        ]
          .filter(Boolean)
          .join(" + ") || "0";

  return (
    <>
      <tr
        style={{ ...S.tr, cursor: "pointer" }}
        onClick={onToggle}
        title="Show people in this organization"
      >
        <td style={{ ...S.td, ...S.tdEmail }}>
          <span style={{ marginRight: 6, color: "#9ca3af" }}>
            {expanded ? "▾" : "▸"}
          </span>
          {org.orgName}
          <div
            style={{
              fontSize: "0.72rem",
              color: "#9ca3af",
              fontFamily: "monospace",
              marginLeft: 16,
            }}
          >
            {org.orgId}
          </div>
        </td>
        <td style={S.td}>{org.people.length}</td>
        <td style={{ ...S.td, ...S.tdMuted }}>{org.via || "—"}</td>
        <td style={S.td}>
          {org.inTrial ? (
            <Pill tone="pending">
              {org.trialDaysLeft === 1
                ? "1 day left"
                : `${org.trialDaysLeft} days left`}
            </Pill>
          ) : org.trialEndsAt ? (
            <Pill tone="accepted">ended</Pill>
          ) : (
            <Pill tone="neutral">none</Pill>
          )}
        </td>
        <td style={S.td}>
          {org.monthsBilled == null ? (
            <span style={S.tdMuted}>—</span>
          ) : (
            org.monthsBilled
          )}
        </td>
        <td style={{ ...S.td, ...S.tdMuted }}>{seatLabel}</td>
        <td style={S.td}>
          {org.inTrial ? (
            <span style={S.tdMuted}>$0 (trial)</span>
          ) : (
            <>
              {fmtAmount(org.monthlyAmount)}
              {org.amountIsEstimated && org.monthlyAmount != null ? (
                <span
                  style={{ ...S.tdMuted, marginLeft: 6 }}
                  title="From the stored subscription, not from mirrored seats"
                >
                  est.
                </span>
              ) : null}
            </>
          )}
        </td>
        <td style={S.td}>{org.pcRuns == null ? "—" : org.pcRuns}</td>
      </tr>

      {expanded ? (
        <tr style={S.tr}>
          <td colSpan={8} style={{ padding: 0, background: "#fafafa" }}>
            <table style={{ ...S.table, background: "transparent" }}>
              <thead>
                <tr>
                  <th style={S.th}>Email</th>
                  <th style={S.th}>Role</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Seat type</th>
                  <th style={S.th}>Signed up</th>
                </tr>
              </thead>
              <tbody>
                {org.people.map((person, idx) => (
                  <tr key={person.email + idx} style={S.tr}>
                    <td style={{ ...S.td, ...S.tdEmail }}>
                      {person.email || "—"}
                    </td>
                    <td style={S.td}>{person.role || "—"}</td>
                    <td style={S.td}>
                      <Pill
                        tone={
                          person.status === "accepted"
                            ? "accepted"
                            : person.status === "pending"
                            ? "pending"
                            : "neutral"
                        }
                      >
                        {person.status || "—"}
                      </Pill>
                    </td>
                    <td style={{ ...S.td, ...S.tdMuted }}>
                      {person.seatType || "standard"}
                    </td>
                    <td style={{ ...S.td, ...S.tdMuted }}>
                      {person.dateSignedUp
                        ? person.dateSignedUp.slice(0, 10)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      ) : null}
    </>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

const SignupsPage = ({ onReady }) => {
  const [env, setEnv] = useState("prod");
  const [source, setSource] = useState("radcrm");
  const [loading, setLoading] = useState(false);
  const [orgRows, setOrgRows] = useState(null);
  const [excludeAddy, setExcludeAddy] = useState(true);
  const [payingOnly, setPayingOnly] = useState(false);
  const [expandedOrgId, setExpandedOrgId] = useState(null);

  useEffect(() => {
    onReady && onReady();
  }, [onReady]);

  const run = useCallback(async () => {
    setLoading(true);
    setOrgRows(null);
    setExpandedOrgId(null);
    try {
      const db = env === "dev" ? firestoreDev : firestoreProd;
      const { rows } = await buildReport(db, source, env);
      setOrgRows(rows);
      if (!rows.length) {
        toast.info("No tagged organizations found for this source.");
      } else {
        const people = rows.reduce((sum, o) => sum + o.people.length, 0);
        toast.success(`Loaded ${rows.length} orgs (${people} sign-ups).`);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      toast.error(err.message || "Failed to load sign-ups");
    } finally {
      setLoading(false);
    }
  }, [env, source]);

  /**
   * Orgs with the Addy team filtered out of their people lists. An org whose
   * only members are Addy staff drops out entirely rather than showing as a
   * customer with zero people.
   */
  const scopedOrgs = useMemo(() => {
    if (!orgRows) return null;

    let out = orgRows.map((org) => ({
      ...org,
      people: excludeAddy ? org.people.filter((p) => !p.isAddy) : org.people,
    }));

    if (excludeAddy) out = out.filter((org) => org.people.length > 0);
    // "Paying" deliberately excludes trialing orgs — they are not customers yet.
    if (payingOnly) out = out.filter((org) => !org.inTrial && org.monthlyAmount);
    return out;
  }, [orgRows, excludeAddy, payingOnly]);

  const addyCount = useMemo(
    () =>
      orgRows
        ? orgRows.reduce(
            (sum, org) => sum + org.people.filter((p) => p.isAddy).length,
            0,
          )
        : 0,
    [orgRows],
  );

  /**
   * Headline numbers. Organizations still inside their free trial are NOT
   * customers yet: they are counted separately and contribute nothing to
   * paying-org count or MRR, so billing metrics reflect real revenue only.
   */
  const stats = useMemo(() => {
    if (!scopedOrgs) return null;

    const billing = scopedOrgs.filter((org) => !org.inTrial);
    return {
      people: scopedOrgs.reduce((sum, org) => sum + org.people.length, 0),
      orgs: scopedOrgs.length,
      trialOrgs: scopedOrgs.filter((org) => org.inTrial).length,
      payingOrgs: billing.filter((org) => org.monthlyAmount > 0).length,
      mrr: billing.reduce((sum, org) => sum + (org.monthlyAmount || 0), 0),
      pcRuns: scopedOrgs.reduce((sum, org) => sum + (org.pcRuns || 0), 0),
    };
  }, [scopedOrgs]);

  return (
    <div className="container-fluid SignupsPageContainer" style={S.page}>
      <ToastContainer position="bottom-right" theme="light" />

      <div style={S.header}>
        <h1 style={S.h1}>
          <span style={S.h1Icon}>
            <FaUserPlus />
          </span>
          Sign-ups by Source
        </h1>
        <p style={S.subtitle}>
          Organizations acquired through a partner link (e.g. RadCRM), with
          their people, free-trial state, months invoiced, and monthly amount.
          Orgs still on trial are excluded from paying-org and MRR figures.
        </p>
      </div>

      <div style={S.controls}>
        <div style={S.fieldsRow}>
          <div style={S.fieldCol}>
            <div style={S.fieldLabel}>Environment</div>
            <select
              value={env}
              onChange={(e) => setEnv(e.target.value)}
              style={S.fieldInput}
            >
              <option value="prod">prod (hey-addy-chatgpt)</option>
              <option value="dev">dev (addy-ai-dev)</option>
            </select>
          </div>
          <div style={S.fieldCol}>
            <div style={S.fieldLabel}>Acquisition source (via)</div>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              style={S.fieldInput}
            >
              {SOURCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={S.actionRow}>
          <button
            onClick={run}
            disabled={loading}
            style={{
              ...S.btnPrimary,
              opacity: loading ? 0.65 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? <FaSync className="signups-spin" /> : <FaFilter />}
            {loading ? "Loading…" : "Run report"}
          </button>
          {scopedOrgs && scopedOrgs.length ? (
            <button
              onClick={() =>
                downloadCsv(
                  `signups-${source}-${env}.csv`,
                  scopedOrgs,
                  excludeAddy,
                )
              }
              style={S.btnGhost}
            >
              <FaDownload /> CSV
            </button>
          ) : null}
          {orgRows && orgRows.length ? (
            <>
              <label style={S.toggleWrap} title="Hides @addy-ai.com members">
                <input
                  type="checkbox"
                  checked={excludeAddy}
                  onChange={(e) => setExcludeAddy(e.target.checked)}
                  style={S.toggleInput}
                />
                <span>
                  Exclude Addy team
                  {addyCount > 0 ? (
                    <span style={S.toggleCount}>({addyCount})</span>
                  ) : null}
                </span>
              </label>
              <label
                style={S.toggleWrap}
                title="Orgs past their free trial with a billable amount"
              >
                <input
                  type="checkbox"
                  checked={payingOnly}
                  onChange={(e) => setPayingOnly(e.target.checked)}
                  style={S.toggleInput}
                />
                <span>Paying orgs only</span>
              </label>
            </>
          ) : null}
        </div>
      </div>

      {stats ? (
        <div style={S.statsRow}>
          <div style={S.statCard("#7f7fd5")}>
            <div style={S.statLabel}>Organizations</div>
            <div style={S.statValue("#7f7fd5")}>{stats.orgs}</div>
            <div style={S.statSub}>
              <FaUsers style={{ marginRight: 5, opacity: 0.6 }} />
              {stats.people} sign-ups
            </div>
          </div>
          <div style={S.statCard("#f59e0b")}>
            <div style={S.statLabel}>On free trial</div>
            <div style={S.statValue("#f59e0b")}>{stats.trialOrgs}</div>
            <div style={S.statSub}>
              <FaHourglassHalf style={{ marginRight: 5, opacity: 0.6 }} />
              not billed yet
            </div>
          </div>
          <div style={S.statCard("#10b981")}>
            <div style={S.statLabel}>Paying orgs</div>
            <div style={S.statValue("#10b981")}>{stats.payingOrgs}</div>
            <div style={S.statSub}>
              <FaBuilding style={{ marginRight: 5, opacity: 0.6 }} />
              excludes trials
            </div>
          </div>
          <div style={S.statCard("#0ea5e9")}>
            <div style={S.statLabel}>MRR</div>
            <div style={S.statValue("#0ea5e9")}>{fmtAmount(stats.mrr)}</div>
            <div style={S.statSub}>
              <FaDollarSign style={{ marginRight: 5, opacity: 0.6 }} />
              billed orgs only
            </div>
          </div>
          <div style={S.statCard("#3b82f6")}>
            <div style={S.statLabel}>PC runs</div>
            <div style={S.statValue("#3b82f6")}>{stats.pcRuns}</div>
            <div style={S.statSub}>
              <FaClipboardCheck style={{ marginRight: 5, opacity: 0.6 }} />
              completed checklist runs
            </div>
          </div>
        </div>
      ) : null}

      {scopedOrgs && scopedOrgs.length ? (
        <div style={S.tableCard}>
          <div style={S.tableHeader}>
            <div style={S.tableTitle}>
              Organizations ({scopedOrgs.length})
            </div>
            <div style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
              Click a row to see its people
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead style={S.thead}>
                <tr>
                  <th style={S.th}>Organization</th>
                  <th style={S.th}>People</th>
                  <th style={S.th}>Via</th>
                  <th style={S.th}>Trial</th>
                  <th style={S.th}>Months billed</th>
                  <th style={S.th}>Seats</th>
                  <th style={S.th}>Per month</th>
                  <th style={S.th}>PC runs</th>
                </tr>
              </thead>
              <tbody>
                {scopedOrgs.map((org) => (
                  <OrgRows
                    key={org.orgId}
                    org={org}
                    expanded={expandedOrgId === org.orgId}
                    onToggle={() =>
                      setExpandedOrgId(
                        expandedOrgId === org.orgId ? null : org.orgId,
                      )
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : loading ? (
        <div style={S.empty}>
          <FaSync className="signups-spin" style={{ fontSize: "1.5rem" }} />
          <div style={{ marginTop: 12 }}>Loading sign-ups…</div>
        </div>
      ) : (
        <div style={S.empty}>
          <div style={{ fontSize: "2rem", marginBottom: 10, opacity: 0.4 }}>
            <FaUserPlus />
          </div>
          Pick a source and click <b>Run report</b>.
        </div>
      )}

      <style>{`
        .signups-spin { animation: signups-spin 1s linear infinite; }
        @keyframes signups-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SignupsPage;
